// Service layer for Supabase operations
import { supabase } from '../config/supabase';

// ========== STORAGE (FOTOS) ==========

/**
 * Upload image to Supabase Storage and return public URL
 */
export const uploadFotoCorretor = async (file, corretorId) => {
  try {
    checkSupabase();
    
    // Validate file
    if (!file || !file.type.startsWith('image/')) {
      throw new Error('Arquivo deve ser uma imagem');
    }
    
    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      throw new Error('Imagem deve ter no máximo 2MB');
    }
    
    // Generate unique filename
    const fileExt = file.name.split('.').pop();
    const fileName = `${corretorId || Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
    const filePath = `corretores/${fileName}`;
    
    // Upload file to Supabase Storage
    const { data, error } = await supabase.storage
      .from('fotos-corretores')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: true // Allow overwriting if file exists
      });
    
    if (error) {
      // If bucket doesn't exist, provide clear instructions
      if (error.message?.includes('Bucket not found') || 
          error.message?.includes('not found') || 
          error.code === '404' ||
          error.statusCode === 404) {
        const errorMsg = 'Bucket "fotos-corretores" não encontrado no Supabase Storage.\n\n' +
          'Por favor, crie o bucket seguindo estes passos:\n' +
          '1. Acesse o Supabase > Storage\n' +
          '2. Clique em "New bucket"\n' +
          '3. Nome: fotos-corretores\n' +
          '4. Marque como "Public bucket"\n' +
          '5. Clique em "Create bucket"\n\n' +
          'Depois, execute o script CRIAR_BUCKET_FOTOS.sql no SQL Editor.';
        console.error(errorMsg);
        throw new Error(errorMsg);
      }
      
      // Handle other storage errors
      if (error.message?.includes('Permission denied') || 
          error.message?.includes('403') ||
          error.statusCode === 403) {
        const errorMsg = 'Permissão negada ao acessar o bucket.\n\n' +
          'Verifique se:\n' +
          '1. O bucket está marcado como "Public"\n' +
          '2. As políticas RLS foram criadas (execute CRIAR_BUCKET_FOTOS.sql)';
        console.error(errorMsg);
        throw new Error(errorMsg);
      }
      
      // Generic error
      console.error('Error uploading foto:', error);
      throw error;
    }
    
    // Get public URL
    const { data: { publicUrl } } = supabase.storage
      .from('fotos-corretores')
      .getPublicUrl(filePath);
    
    return publicUrl;
  } catch (error) {
    console.error('Error uploading foto:', error);
    throw error;
  }
};

/**
 * Delete foto from Supabase Storage
 */
export const deleteFotoCorretor = async (fotoUrl) => {
  try {
    checkSupabase();
    
    if (!fotoUrl || !fotoUrl.includes('/storage/v1/object/public/fotos-corretores/')) {
      // Not a storage URL, might be base64 or external URL
      return;
    }
    
    // Extract file path from URL
    const urlParts = fotoUrl.split('/storage/v1/object/public/fotos-corretores/');
    if (urlParts.length < 2) return;
    
    const filePath = urlParts[1];
    
    // Delete file
    const { error } = await supabase.storage
      .from('fotos-corretores')
      .remove([filePath]);
    
    if (error) {
      console.error('Error deleting foto:', error);
      // Don't throw - deletion is not critical
    }
  } catch (error) {
    console.error('Error deleting foto:', error);
    // Don't throw - deletion is not critical
  }
};

// Helper function to check if Supabase is configured
const checkSupabase = () => {
  if (!supabase) {
    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
    const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
    
    const missing = [];
    if (!supabaseUrl) missing.push('REACT_APP_SUPABASE_URL');
    if (!supabaseKey) missing.push('REACT_APP_SUPABASE_ANON_KEY');
    
    throw new Error(`Supabase não está configurado. Configure as variáveis de ambiente: ${missing.join(', ')}`);
  }
};

// ========== CORRETORES ==========

/**
 * Get all corretores
 */
export const getCorretores = async (retryCount = 0) => {
  const MAX_RETRIES = 1; // Reduzido de 2 para 1 para evitar múltiplas tentativas
  try {
    checkSupabase();
    
    // Otimizado: selecionar apenas campos necessários ao invés de '*'
    const { data, error } = await supabase
      .from('corretores')
      .select('id, nome, email, telefone, creci, foto, ativo, created_at, updated_at')
      .order('nome', { ascending: true });

    if (error) {
      console.error('getCorretores: Supabase error:', error);
      
      // Retry on timeout errors
      if (error.message?.includes('timeout') || error.message?.includes('canceling statement') || error.code === 'PGRST301') {
        if (retryCount < MAX_RETRIES) {
          await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1))); // Exponential backoff
          return getCorretores(retryCount + 1);
        }
      }
      
      throw error;
    }
    
    return data || [];
  } catch (error) {
    console.error('getCorretores: Error fetching corretores:', error);
    
    // Check for CORS or network errors
    const isCorsError = error.message?.includes('CORS') || 
                       error.message?.includes('Failed to fetch') ||
                       error.message?.includes('NetworkError') ||
                       error.name === 'TypeError';
    
    if (isCorsError) {
      console.error('getCorretores: CORS or network error detected. This usually means:');
      console.error('1. Supabase URL or key might be incorrect');
      console.error('2. RLS policies might be blocking the request');
      console.error('3. Network connectivity issue');
      console.error('Error details:', error);
      throw new Error('Erro de conexão com o banco de dados. Verifique as configurações do Supabase e as políticas RLS.');
    }
    
    // Retry on timeout errors
    if ((error.message?.includes('timeout') || error.message?.includes('canceling statement') || error.message?.includes('Query timeout')) && retryCount < MAX_RETRIES) {
      await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)));
      return getCorretores(retryCount + 1);
    }
    
    throw error;
  }
};

/**
 * Get a single corretor by ID
 */
export const getCorretorById = async (id) => {
  try {
    checkSupabase();
    const { data, error } = await supabase
      .from('corretores')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching corretor:', error);
    throw error;
  }
};

/**
 * Create a new corretor
 */
export const createCorretor = async (corretor) => {
  try {
    checkSupabase();
    
    // Garantir que foto seja apenas URL (http/https) ou null - nunca base64
    let fotoUrl = null;
    if (corretor.foto) {
      if (typeof corretor.foto === 'string' && (corretor.foto.startsWith('http://') || corretor.foto.startsWith('https://'))) {
        fotoUrl = corretor.foto;
      } else if (typeof corretor.foto === 'string' && corretor.foto.startsWith('data:')) {
        // Base64 não deve ser salvo - apenas URLs
        console.warn('createCorretor: Tentativa de salvar base64 ignorada. Use upload para Storage primeiro.');
        fotoUrl = null;
      } else {
        fotoUrl = corretor.foto; // Pode ser null ou outra string válida
      }
    }
    
    const { data, error } = await supabase
      .from('corretores')
      .insert([{
        nome: corretor.nome,
        email: corretor.email,
        telefone: corretor.telefone || null,
        creci: corretor.creci || null,
        foto: fotoUrl, // Apenas URL ou null
        ativo: corretor.ativo !== undefined ? corretor.ativo : true,
      }])
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating corretor:', error);
    throw error;
  }
};

/**
 * Update a corretor
 */
export const updateCorretor = async (id, updates) => {
  try {
    checkSupabase();
    
    // Garantir que foto seja apenas URL (http/https) ou null - nunca base64
    if (updates.foto !== undefined) {
      if (updates.foto && typeof updates.foto === 'string') {
        if (updates.foto.startsWith('data:')) {
          // Base64 não deve ser salvo - apenas URLs
          console.warn('updateCorretor: Tentativa de salvar base64 ignorada. Use upload para Storage primeiro.');
          updates.foto = null; // Não salvar base64
        } else if (!updates.foto.startsWith('http://') && !updates.foto.startsWith('https://')) {
          // Se não for URL válida, definir como null
          console.warn('updateCorretor: Foto não é uma URL válida, definindo como null');
          updates.foto = null;
        }
        // Se for URL válida (http/https), manter como está
      }
    }
    
    const { data, error } = await supabase
      .from('corretores')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating corretor:', error);
    throw error;
  }
};

/**
 * Delete a corretor
 */
export const deleteCorretor = async (id) => {
  try {
    checkSupabase();
    const { error } = await supabase
      .from('corretores')
      .delete()
      .eq('id', id);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error deleting corretor:', error);
    throw error;
  }
};

/**
 * Toggle corretor active status
 */
export const toggleCorretorAtivo = async (id, ativo) => {
  return updateCorretor(id, { ativo });
};

// ========== VENDAS ==========

/**
 * Get all vendas for a corretor
 */
export const getVendasByCorretor = async (corretorId) => {
  try {
    checkSupabase();
    const { data, error } = await supabase
      .from('vendas')
      .select('*')
      .eq('corretor_id', corretorId)
      .order('ano', { ascending: false })
      .order('mes', { ascending: true });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching vendas:', error);
    throw error;
  }
};

/**
 * Get vendas by ano and mes
 */
export const getVendasByAnoMes = async (ano, mes) => {
  try {
    checkSupabase();
    
    // Ensure mes is a month name (not number)
    let mesNome = mes;
    if (typeof mes === 'number' || (typeof mes === 'string' && /^\d+$/.test(mes))) {
      const meses = [
        'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
        'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
      ];
      const num = typeof mes === 'number' ? mes : parseInt(mes, 10);
      if (num >= 1 && num <= 12) {
        mesNome = meses[num - 1];
      }
    }
    
    const { data, error } = await supabase
      .from('vendas')
      .select('*, corretores(*)')
      .eq('ano', ano)
      .eq('mes', mesNome); // Use month name

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching vendas by ano/mes:', error);
    throw error;
  }
};

/**
 * Get all vendas with corretores data
 */
export const getAllVendas = async (retryCount = 0) => {
  const MAX_RETRIES = 1; // Reduzido de 2 para 1 para evitar múltiplas tentativas
  try {
    checkSupabase();
    
    // Otimizado: JOIN apenas com campo nome (não todos os campos do corretor)
    // Filtrar apenas anos recentes (últimos 3 anos) para reduzir egress
    const currentYear = new Date().getFullYear();
    const minYear = currentYear - 2; // Últimos 3 anos (currentYear, currentYear-1, currentYear-2)
    
    const { data, error } = await supabase
      .from('vendas')
      .select(`
        corretor_id, 
        ano, 
        mes, 
        valor, 
        pontuacao_meta,
        agenciamento,
        numero_contratos,
        corretores!inner(nome)
      `)
      .gte('ano', minYear) // Apenas anos >= minYear (últimos 3 anos)
      .order('ano', { ascending: false })
      .order('mes', { ascending: true })
      .limit(5000); // Reduzido de 10000 para 5000 para economizar egress

    if (error) {
      console.error('getAllVendas: Supabase error:', error);
      
      // Retry on timeout errors
      if (error.message?.includes('timeout') || error.message?.includes('canceling statement') || error.code === 'PGRST301') {
        if (retryCount < MAX_RETRIES) {
          await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)));
          return getAllVendas(retryCount + 1);
        }
      }
      
      throw error;
    }
    
    return data || [];
  } catch (error) {
    console.error('getAllVendas: Error fetching all vendas:', error);
    
    // Check for CORS or network errors
    const isCorsError = error.message?.includes('CORS') || 
                       error.message?.includes('Failed to fetch') ||
                       error.message?.includes('NetworkError') ||
                       error.name === 'TypeError';
    
    if (isCorsError) {
      console.error('getAllVendas: CORS or network error detected. This usually means:');
      console.error('1. Supabase URL or key might be incorrect');
      console.error('2. RLS policies might be blocking the request');
      console.error('3. Network connectivity issue');
      console.error('Error details:', error);
      throw new Error('Erro de conexão com o banco de dados. Verifique as configurações do Supabase e as políticas RLS.');
    }
    
    // Retry on timeout errors
    if ((error.message?.includes('timeout') || error.message?.includes('canceling statement')) && retryCount < MAX_RETRIES) {
      await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)));
      return getAllVendas(retryCount + 1);
    }
    
    throw error;
  }
};

/**
 * Convert month name to month number
 */
const mesNomeParaNumero = (mesNome) => {
  const meses = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];
  
  // If it's already a number, return it
  if (typeof mesNome === 'number') {
    return mesNome;
  }
  
  // If it's a string number, parse it
  const num = parseInt(mesNome, 10);
  if (!isNaN(num) && num >= 1 && num <= 12) {
    return num;
  }
  
  // If it's a month name, find its index
  const index = meses.findIndex(m => m.toLowerCase() === mesNome.toLowerCase());
  if (index !== -1) {
    return index + 1; // Return 1-12
  }
  
  // Fallback: return as is (might cause error, but at least we tried)
  return mesNome;
};

/**
 * Create or update a venda
 */
export const upsertVenda = async (
  corretorId,
  ano,
  mes,
  valor,
  agenciamento = 0,
  numeroContratos = 0,
  pontuacaoMeta = 0
) => {
  try {
    checkSupabase();
    
    // Ensure mes is a month name (not number)
    // If it's a number, convert to name; if it's already a name, use it
    let mesNome = mes;
    if (typeof mes === 'number' || (typeof mes === 'string' && /^\d+$/.test(mes))) {
      const meses = [
        'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
        'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
      ];
      const num = typeof mes === 'number' ? mes : parseInt(mes, 10);
      if (num >= 1 && num <= 12) {
        mesNome = meses[num - 1];
      }
    }
    
    const { data, error } = await supabase
      .from('vendas')
      .upsert({
        corretor_id: corretorId,
        ano: ano,
        mes: mesNome, // Save as month name, not number
        valor: valor,
        pontuacao_meta: pontuacaoMeta,
        agenciamento: agenciamento,
        numero_contratos: numeroContratos,
      }, {
        onConflict: 'corretor_id,ano,mes',
      })
      .select()
      .single();

    if (error) {
      console.error('upsertVenda: Erro do Supabase:', error);
      throw error;
    }
    
    return data;
  } catch (error) {
    console.error('upsertVenda: Erro ao salvar venda no banco:', error);
    throw error;
  }
};

/**
 * Batch upsert multiple vendas at once (optimized for reducing egress)
 */
export const upsertVendasBatch = async (vendasArray) => {
  try {
    checkSupabase();
    
    if (!vendasArray || vendasArray.length === 0) {
      return [];
    }
    
    // Convert all meses to month names
    const vendasProcessadas = vendasArray.map(venda => {
      let mesNome = venda.mes;
      if (typeof venda.mes === 'number' || (typeof venda.mes === 'string' && /^\d+$/.test(venda.mes))) {
        const meses = [
          'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
          'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
        ];
        const num = typeof venda.mes === 'number' ? venda.mes : parseInt(venda.mes, 10);
        if (num >= 1 && num <= 12) {
          mesNome = meses[num - 1];
        }
      }
      
      return {
        corretor_id: venda.corretorId,
        ano: venda.ano,
        mes: mesNome,
        valor: venda.valor || 0,
        pontuacao_meta: venda.pontuacaoMeta || 0,
        agenciamento: venda.agenciamento || 0,
        numero_contratos: venda.numeroContratos || 0,
      };
    });
    
    const { data, error } = await supabase
      .from('vendas')
      .upsert(vendasProcessadas, {
        onConflict: 'corretor_id,ano,mes',
      })
      .select();
    
    if (error) {
      console.error('upsertVendasBatch: Erro do Supabase:', error);
      throw error;
    }
    
    return data || [];
  } catch (error) {
    console.error('upsertVendasBatch: Erro ao salvar vendas em lote:', error);
    throw error;
  }
};

/**
 * Delete a venda
 */
export const deleteVenda = async (corretorId, ano, mes) => {
  try {
    checkSupabase();
    
    // Ensure mes is a month name (not number)
    let mesNome = mes;
    if (typeof mes === 'number' || (typeof mes === 'string' && /^\d+$/.test(mes))) {
      const meses = [
        'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
        'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
      ];
      const num = typeof mes === 'number' ? mes : parseInt(mes, 10);
      if (num >= 1 && num <= 12) {
        mesNome = meses[num - 1];
      }
    }
    
    const { error } = await supabase
      .from('vendas')
      .delete()
      .eq('corretor_id', corretorId)
      .eq('ano', ano)
      .eq('mes', mesNome); // Use month name

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error deleting venda:', error);
    throw error;
  }
};

// ========== CONFIGURAÇÕES ==========

/**
 * Get configuration value by key
 */
export const getConfig = async (chave) => {
  try {
    checkSupabase();
    const { data, error } = await supabase
      .from('configuracoes')
      .select('valor')
      .eq('chave', chave)
      .maybeSingle(); // Use maybeSingle() instead of single() to handle 0 rows gracefully

    // If error is PGRST116 (0 rows), return null instead of throwing
    if (error) {
      // PGRST116 = "The result contains 0 rows" - this is expected when config doesn't exist
      if (error.code === 'PGRST116') {
        return null;
      }
      throw error;
    }
    
    return data?.valor || null;
  } catch (error) {
    // Only log non-PGRST116 errors
    if (error.code !== 'PGRST116') {
      console.error('Error fetching config:', error);
    }
    // Return null for any error (including PGRST116) to allow app to continue
    return null;
  }
};

/**
 * Get multiple configuration values at once (optimized to reduce egress)
 */
export const getConfigs = async (chaves) => {
  try {
    checkSupabase();
    if (!chaves || chaves.length === 0) {
      return {};
    }
    
    const { data, error } = await supabase
      .from('configuracoes')
      .select('chave, valor')
      .in('chave', chaves);

    if (error) {
      console.error('Error fetching configs:', error);
      return {};
    }
    
    // Convert array to object
    const configs = {};
    if (data) {
      data.forEach(item => {
        configs[item.chave] = item.valor;
      });
    }
    
    return configs;
  } catch (error) {
    console.error('Error fetching configs:', error);
    return {};
  }
};

/**
 * Set configuration value
 */
export const setConfig = async (chave, valor) => {
  try {
    checkSupabase();
    
    // Adicionar timeout de 10 segundos para cada requisição (aumentado de 5 para 10)
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Timeout ao salvar configuração ${chave}: a requisição demorou mais de 10 segundos`));
      }, 10000);
    });
    
    const upsertPromise = supabase
      .from('configuracoes')
      .upsert({
        chave: chave,
        valor: valor.toString(),
      }, {
        onConflict: 'chave',
      })
      .select()
      .single();
    
    // Wrapper para tornar o upsertPromise uma Promise que pode ser rejeitada
    const upsertWithTimeout = Promise.race([
      upsertPromise.then(result => {
        if (result.error) {
          throw result.error;
        }
        return result.data;
      }),
      timeoutPromise
    ]);
    
    const data = await upsertWithTimeout;
    
    // Também salvar no localStorage como backup
    try {
      localStorage.setItem(`config_${chave}`, valor.toString());
    } catch (localError) {
      console.warn('Erro ao salvar no localStorage (não crítico):', localError);
    }
    
    return data;
  } catch (error) {
    console.error('Error setting config no Supabase:', error);
    
    // Fallback: salvar no localStorage se o Supabase falhar
    try {
      localStorage.setItem(`config_${chave}`, valor.toString());
      
      // Retornar um objeto simulado para não quebrar o código
      return { chave, valor: valor.toString() };
    } catch (localError) {
      console.error('Error setting config no localStorage:', localError);
      throw new Error(`Não foi possível salvar ${chave}: Supabase falhou e localStorage também falhou`);
    }
  }
};

// ========== HELPER FUNCTIONS ==========

/**
 * Map month number to month name
 */
const mesNumeroParaNome = (numero) => {
  const meses = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];
  
  // If it's already a string, check if it's a number as string
  if (typeof numero === 'string') {
    const num = parseInt(numero, 10);
    if (!isNaN(num) && num >= 1 && num <= 12) {
      return meses[num - 1];
    }
    // If it's already a month name, return it
    return numero;
  }
  
  // If it's a number, convert to month name
  if (typeof numero === 'number' && numero >= 1 && numero <= 12) {
    return meses[numero - 1];
  }
  
  // Fallback: return as is
  return numero;
};

/**
 * Transform vendas array to the format expected by the app
 */
export const transformVendasToAppFormat = (vendas) => {
  const vendasByCorretor = {};
  
  vendas.forEach(venda => {
    const corretorId = venda.corretor_id;
    if (!vendasByCorretor[corretorId]) {
      vendasByCorretor[corretorId] = {};
    }
    
    const ano = venda.ano;
    if (!vendasByCorretor[corretorId][ano]) {
      vendasByCorretor[corretorId][ano] = {};
    }
    
    // mes should already be a month name, but convert if it's a number (for backward compatibility)
    let mesNome = venda.mes;
    if (typeof venda.mes === 'number' || (typeof venda.mes === 'string' && /^\d+$/.test(venda.mes))) {
      mesNome = mesNumeroParaNome(venda.mes);
    }
    
    // Store valor, agenciamento and numero_contratos
    vendasByCorretor[corretorId][ano][mesNome] = {
      valor: parseFloat(venda.valor || 0),
      pontuacao_meta: parseInt(venda.pontuacao_meta || 0, 10),
      agenciamento: parseInt(venda.agenciamento || 0, 10), // Integer, not decimal
      numero_contratos: parseInt(venda.numero_contratos || 0, 10), // Integer, not decimal
    };
  });
  
  return vendasByCorretor;
};

/**
 * Transform corretores with vendas to app format
 */
export const transformCorretoresWithVendas = (corretores, vendas) => {
  if (!corretores || !Array.isArray(corretores)) {
    return [];
  }
  
  if (!vendas || !Array.isArray(vendas)) {
    // Continue with empty vendas array
  }
  
  const vendasByCorretor = transformVendasToAppFormat(vendas || []);
  
  const transformed = corretores.map(corretor => ({
    id: corretor.id,
    nome: corretor.nome,
    email: corretor.email,
    telefone: corretor.telefone,
    creci: corretor.creci,
    foto: corretor.foto,
    ativo: corretor.ativo !== undefined ? corretor.ativo : true,
    vendas: vendasByCorretor[corretor.id] || {},
  }));
  
  return transformed;
};

